// frontend/src/pages/Login.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PasswordInput from '../components/PasswordInput';
import {
  Flex,
  Stack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  Alert,
  AlertIcon,
  Image,
  Card,
  CardBody,
  Divider,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import WebAuthnSetup from '../components/WebAuthnSetup';
import { startAuthentication } from '@simplewebauthn/browser';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const supported = typeof window !== 'undefined' && !!window.PublicKeyCredential;

const Login = () => {
  const savedEmail = localStorage.getItem('lastLoginEmail') || '';
  const [email, setEmail] = useState(savedEmail);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [bioLoading, setBioLoading] = useState(false);
  const [bioError, setBioError] = useState('');
  const [hasBio, setHasBio] = useState(false);
  const [showBioSetup, setShowBioSetup] = useState(false);
  const pendingNav = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Message si redirigé ici suite à l'expiration de la session
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('expired') === '1') {
      setError('Votre session a expiré, veuillez vous reconnecter.');
    }
  }, []);

  // Vérifier si l'email saisi a une biométrie enregistrée
  useEffect(() => {
    if (!email || !supported) { setHasBio(false); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/webauthn/login/begin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        setHasBio(res.ok);
      } catch { setHasBio(false); }
    }, 600);
    return () => clearTimeout(t);
  }, [email]);

  async function handleBiometric() {
    setBioLoading(true);
    setBioError('');
    try {
      const res = await fetch(`${API_BASE}/webauthn/login/begin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      const opts = await res.json();

      // La lib gère les conversions et l'appel navigateur, et renvoie la réponse complète (signature incluse)
      const credential = await startAuthentication(opts);

      const payload = { email, credential };
      const finish = await fetch(`${API_BASE}/webauthn/login/finish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!finish.ok) throw new Error((await finish.json()).error);
      const data = await finish.json();
      localStorage.setItem('lastLoginEmail', email);
      login(data.token, data.user);
      navigate(data.user.is_admin ? '/dashboard' : '/blog');
    } catch (e) {
      if (e.name !== 'NotAllowedError') setBioError(e.message || 'Échec de l\'authentification biométrique');
    } finally {
      setBioLoading(false);
    }
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await apiService.login(email, password);
      
      // Connexion réussie normale
      localStorage.setItem('lastLoginEmail', email);
      login(response.token, response.user);

      // Proposer l'enregistrement biométrique si supporté et pas encore configuré
      if (supported) {
        try {
          const chk = await fetch(`${API_BASE}/webauthn/login/begin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          if (!chk.ok) {
            // Pas de biométrie → proposer l'activation avant de rediriger
            pendingNav.current = response.user.is_admin ? '/dashboard' : '/blog';
            setShowBioSetup(true);
            setIsLoading(false);
            return;
          }
        } catch {}
      }

      // Rediriger vers le dashboard ou membres
      if (response.user.is_admin) {
        navigate('/dashboard');
      } else {
        navigate('/blog');
      }
    } catch (err) {
      setError(err.message || 'Erreur de connexion');
      setIsLoading(false);
    }
  };
  
  // Formulaire de connexion standard
  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg="gray.50"
      px={4}
    >
      <Stack spacing={8} mx="auto" maxW="lg" py={12} px={6} w="full">
        <Stack align="center">
          <Image 
            src="/logo-eecf.png" 
            alt="EECF Logo"
            width="80px"
            height="80px"
            fallbackSrc="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDAiIGN5PSI0MCIgcj0iMzIiIHN0cm9rZT0iIzFGNzA0MCIgc3Ryb2tlLXdpZHRoPSI0IiBmaWxsPSIjMkRDNjUzIi8+Cjx0ZXh0IHg9IjQwIiB5PSI0NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0id2hpdGUiIGZvbnQtc2l6ZT0iMTQiIGZvbnQtd2VpZ2h0PSJib2xkIj5FRUNGPC90ZXh0Pgo8L3N2Zz4K"
            mb={4}
          />
          <Heading fontSize="4xl" color="eecf.500">
            Mon Compte EECF
          </Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center">
            Interface de gestion de votre espace membre
          </Text>
        </Stack>

        <Card>
          <CardBody>
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                {error && (
                  <Alert status="error">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}

                <FormControl id="email" isRequired>
                  <FormLabel>Adresse email</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre.email@exemple.com"
                  />
                </FormControl>

                <FormControl id="password" isRequired>
                  <FormLabel>Mot de passe</FormLabel>
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Première connexion ? Demandez votre mot de passe provisoire à l'administration, ou utilisez « Mot de passe oublié ».
                  </Text>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="green"
                  size="lg"
                  fontSize="md"
                  isLoading={isLoading}
                  loadingText="Connexion..."
                >
                  Se connecter
                </Button>

                {supported && hasBio && (
                  <>
                    <Divider />
                    {bioError && (
                      <Alert status="error" py={2} fontSize="sm">
                        <AlertIcon />
                        {bioError}
                      </Alert>
                    )}
                    <Button
                      variant="outline"
                      colorScheme="green"
                      size="lg"
                      onClick={handleBiometric}
                      isLoading={bioLoading}
                      loadingText="Biométrie..."
                    >
                      🔐 Se connecter avec la biométrie
                    </Button>
                  </>
                )}

                <Button
                  variant="link"
                  colorScheme="blue"
                  size="sm"
                  onClick={() => navigate('/forgot-password')}
                >
                  Mot de passe oublié ?
                </Button>
              </Stack>
            </form>
          </CardBody>
        </Card>

        <Text fontSize="sm" color="gray.500" textAlign="center">
          Pas encore membre ?{' '}
          <Button
            variant="link"
            colorScheme="green"
            size="sm"
            onClick={() => navigate('/enrollment')}
          >
            Faire une demande d'adhésion
          </Button>
        </Text>
      </Stack>

      {/* Modal proposition biométrie après login */}
      <WebAuthnSetup
        isOpen={showBioSetup}
        onClose={() => {
          setShowBioSetup(false);
          // naviguer après fermeture du modal
          const dest = pendingNav.current;
          if (dest) navigate(dest);
        }}
      />
    </Flex>
  );
};

export default Login;
